<template>
  <v-container fluid class="wallet-page">
    <div class="wallet-shell">
      <div class="wallet-header">
        <div class="wallet-heading">
          <h1 class="wallet-title">Wallet cards</h1>
          <p class="wallet-meta">
            {{ cards.length }} active {{ cards.length === 1 ? "card" : "cards" }}
          </p>
        </div>
        <v-btn
          variant="text"
          color="black"
          class="wallet-back-btn"
          :to="user && user.is_vip ? '/customer/profile' : '/profile'"
        >
          Back to profile
        </v-btn>
      </div>

      <v-alert v-if="error" type="error" class="mb-4">
        Could not load wallet cards right now.
      </v-alert>

      <div v-if="loading" class="wallet-loading">
        <v-skeleton-loader type="article, actions" class="wallet-skeleton" />
      </div>

      <template v-else>
        <div class="wallet-content">
          <div v-if="cards.length" class="wallet-stage">
            <div
              :class="['wallet-rail', { 'wallet-rail--single': cards.length === 1 }]"
              ref="rail"
              @scroll.passive="onRailScroll"
            >
              <article
                v-for="card in cards"
                :key="card.stack_id"
                :class="['wallet-card', { 'wallet-card--image': !!card.image }]"
                :style="cardStyle(card)"
              >
                <div class="wallet-card-top">
                  <div class="wallet-card-chips-row">
                    <span class="wallet-expiry-chip">{{ formatExpiry(card.expiration) }}</span>
                    <span class="wallet-qty-chip">x{{ card.quantity }}</span>
                  </div>
                  <div v-if="!card.image">
                    <p class="wallet-bar">{{ card.displayBar }}</p>
                    <p class="wallet-address">{{ card.address }}</p>
                  </div>
                </div>

                <div class="wallet-card-middle">
                  <div class="wallet-offer-pill">{{ card.discount_text }}</div>
                </div>

                <div v-if="card.lots && card.lots.length" class="wallet-lots-summary">
                  <p
                    v-for="(line, idx) in lotSummaryLines(card)"
                    :key="`lot-${card.stack_id}-${idx}`"
                    class="wallet-lot-line"
                  >
                    {{ line }}
                  </p>
                  <div v-if="remainingLotCardCount(card) > 0" class="wallet-lot-more-row">
                    <p class="wallet-lot-line wallet-lot-line--muted">
                      +{{ remainingLotCardCount(card) }} expiring later
                    </p>
                    <button
                      type="button"
                      class="wallet-lot-info-btn"
                      @click.stop="openLotDetails(card)"
                      aria-label="Show full expiry breakdown"
                    >
                      <span aria-hidden="true">i</span>
                    </button>
                  </div>
                </div>

                <div class="wallet-card-bottom">
                  <v-btn
                    class="wallet-redeem-btn"
                    color="black"
                    variant="flat"
                    :loading="redeemingKey === card.stack_id"
                    @click="handleRedeem(card)"
                  >
                    Redeem
                  </v-btn>
                </div>
              </article>
            </div>

          </div>
          <div v-if="cards.length" class="wallet-stage-divider" aria-hidden="true"></div>
          <div v-if="cards.length > 1" class="wallet-dots" aria-label="Wallet card pagination">
            <button
              v-for="(_, i) in cards"
              :key="`dot-${i}`"
              type="button"
              :class="['wallet-dot', { 'wallet-dot--active': i === activeIndex }]"
              :aria-label="`Go to card ${i + 1}`"
              @click="scrollToCard(i)"
            />
          </div>

          <div v-if="!cards.length" class="wallet-empty">
            <p>No active wallet cards.</p>
          </div>

          <div class="wallet-mobile-footer">
            <v-btn
              variant="text"
              color="black"
              class="wallet-mobile-back-btn"
              :to="user && user.is_vip ? '/customer/profile' : '/profile'"
            >
              Back to profile
            </v-btn>
          </div>

        </div>
      </template>

      <v-dialog v-model="lotDialogOpen" max-width="420">
        <v-card>
          <v-card-title class="text-h6">Expiry breakdown</v-card-title>
          <v-card-text>
            <p v-if="lotDialogCard" class="wallet-lot-dialog-title">
              {{ lotDialogCard.displayBar }} · {{ lotDialogCard.discount_text }}
            </p>
            <ul v-if="lotDialogCard" class="wallet-lot-dialog-list">
              <li v-for="(line, idx) in lotDetailLines(lotDialogCard)" :key="`lot-detail-${idx}`">
                {{ line }}
              </li>
            </ul>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="lotDialogOpen = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="redeemDialogOpen" max-width="420">
        <v-card>
          <v-card-title class="text-h6">Redeem wallet card</v-card-title>
          <v-card-text>
            <p v-if="redeemDialogCard" class="wallet-lot-dialog-title">
              {{ redeemDialogCard.displayBar }} · {{ redeemDialogCard.discount_text }}
            </p>
            <p v-if="redeemDialogCard" class="mb-3">
              Available: {{ redeemDialogCard.quantity }}
            </p>
            <div class="wallet-redeem-stepper">
              <v-btn
                size="small"
                variant="outlined"
                :disabled="redeemDialogAmount <= 1 || !!redeemingKey || redeemSubmitLock"
                @click="decreaseRedeemAmount"
              >
                -
              </v-btn>
              <span class="wallet-redeem-amount">{{ redeemDialogAmount }}</span>
              <v-btn
                size="small"
                variant="outlined"
                :disabled="!redeemDialogCard || redeemDialogAmount >= redeemDialogCard.quantity || !!redeemingKey || redeemSubmitLock"
                @click="increaseRedeemAmount"
              >
                +
              </v-btn>
            </div>
            <p v-if="redeemDialogError" class="wallet-redeem-error">{{ redeemDialogError }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="closeRedeemDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              variant="flat"
              :loading="redeemDialogCard && redeemingKey === redeemDialogCard.stack_id"
              :disabled="redeemSubmitLock || (redeemDialogCard && redeemingKey === redeemDialogCard.stack_id)"
              @click="confirmRedeemDialog"
            >
              Redeem
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import templeCard from "@/assets/wallet-cards-nopill/temple-bar.png";
import stefanshusCard from "@/assets/wallet-cards-nopill/stefanshus.png";
import understelletCard from "@/assets/wallet-cards-nopill/understellet.png";
import klovnensCard from "@/assets/wallet-cards-nopill/klovnens.png";

const BAR_META = {
  "Temple Bar": {
    displayBar: "Temple Bar",
    address: "Nørrebrogade 48",
    image: templeCard,
  },
  "Stefanshus": {
    displayBar: "Café Stefanshus",
    address: "Stefansgade 22",
    image: stefanshusCard,
  },
  "Understellet": {
    displayBar: "Understellet",
    address: "Nørrebrogade 22",
    image: understelletCard,
  },
  "Klovnen": {
    displayBar: "Klovnens Bodega",
    address: "Rantzausgade 70",
    image: klovnensCard,
  },
  "Klovnens Bodega": {
    displayBar: "Klovnens Bodega",
    address: "Rantzausgade 70",
    image: klovnensCard,
  },
};

export default {
  name: "WalletPage",
  data() {
    return {
      user: null,
      cards: [],
      loading: true,
      error: false,
      redeemingKey: null,
      activeIndex: 0,
      lotDialogOpen: false,
      lotDialogCard: null,
      redeemDialogOpen: false,
      redeemDialogCard: null,
      redeemDialogAmount: 1,
      redeemDialogError: "",
      redeemSubmitLock: false,
      redeemRequestId: null,
    };
  },
  async created() {
    await this.bootstrapWallet();
  },
  methods: {
    async bootstrapWallet() {
      this.loading = true;
      this.error = false;
      try {
        const [userRes, cardsRes] = await Promise.all([
          axios.get("/api/user/", { withCredentials: true }),
          axios.get("/api/wallet/cards/", { withCredentials: true }),
        ]);
        this.user = userRes.data || null;
        const raw = Array.isArray(cardsRes.data) ? cardsRes.data : [];
        this.cards = raw
          .map((d) => {
            const meta = BAR_META[d.bar] || {
              displayBar: d.bar || "Unknown Bar",
              address: "Star Group",
              image: "",
            };
            return {
              ...d,
              displayBar: meta.displayBar,
              address: meta.address,
              image: meta.image,
              quantity: Number(d.quantity || 1),
              lots: Array.isArray(d.lots) ? d.lots : [],
            };
          });
      } catch (e) {
        console.error("Error loading wallet:", e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatExpiry(expiration) {
      if (!expiration) return "No expiry";
      const d = new Date(expiration);
      return `Expires ${d.toLocaleDateString()}`;
    },
    formatLotExpiration(expiration) {
      if (!expiration) return "no expiry";
      return new Date(expiration).toLocaleDateString();
    },
    lotSummaryLines(card) {
      const lots = Array.isArray(card.lots) ? card.lots.slice(0, 2) : [];
      return lots.map((lot) => {
        if (!lot.expiration) return `${lot.quantity} no expiry`;
        return `${lot.quantity} expiring ${this.formatLotExpiration(lot.expiration)}`;
      });
    },
    remainingLotGroups(card) {
      const len = Array.isArray(card.lots) ? card.lots.length : 0;
      return Math.max(0, len - 2);
    },
    remainingLotCardCount(card) {
      if (!Array.isArray(card.lots) || card.lots.length <= 2) return 0;
      return card.lots.slice(2).reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);
    },
    lotDetailLines(card) {
      const lots = Array.isArray(card.lots) ? card.lots : [];
      return lots.map((lot) => {
        if (!lot.expiration) return `${lot.quantity} no expiry`;
        return `${lot.quantity} expiring ${this.formatLotExpiration(lot.expiration)}`;
      });
    },
    openLotDetails(card) {
      this.lotDialogCard = card;
      this.lotDialogOpen = true;
    },
    handleRedeem(card) {
      this.redeemDialogCard = card;
      this.redeemDialogAmount = 1;
      this.redeemDialogError = "";
      this.redeemRequestId = null;
      this.redeemDialogOpen = true;
    },
    closeRedeemDialog(force = false) {
      if (this.redeemingKey && !force) return;
      this.redeemDialogOpen = false;
      this.redeemDialogCard = null;
      this.redeemDialogAmount = 1;
      this.redeemDialogError = "";
      this.redeemRequestId = null;
    },
    increaseRedeemAmount() {
      if (!this.redeemDialogCard) return;
      const max = this.redeemDialogCard.quantity;
      this.redeemDialogAmount = Math.min(max, this.redeemDialogAmount + 1);
      this.redeemDialogError = "";
    },
    decreaseRedeemAmount() {
      this.redeemDialogAmount = Math.max(1, this.redeemDialogAmount - 1);
      this.redeemDialogError = "";
    },
    async confirmRedeemDialog() {
      if (this.redeemingKey || this.redeemSubmitLock) return;
      const card = this.redeemDialogCard;
      if (!card) return;
      const amount = Number(this.redeemDialogAmount);
      if (!Number.isInteger(amount) || amount < 1 || amount > card.quantity) {
        this.redeemDialogError = `Enter a whole number from 1 to ${card.quantity}.`;
        return;
      }
      if (!this.redeemRequestId) {
        this.redeemRequestId =
          window?.crypto?.randomUUID?.() ||
          `redeem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
      const requestedFromQty = card.quantity;

      this.redeemSubmitLock = true;
      this.redeemingKey = card.stack_id;
      this.redeemDialogError = "";
      try {
        const res = await axios.post(
          "/api/wallet/redeem/",
          {
            discount_template_id: card.discount_template_id,
            bar: card.bar,
            amount,
            idempotency_key: this.redeemRequestId,
          },
          { withCredentials: true }
        );
        if (res.data && res.data.success) {
          // Re-fetch to sync quantity + lot expirations exactly after redemption.
          await this.bootstrapWallet();
          this.closeRedeemDialog(true);
          if (this.activeIndex >= this.cards.length) {
            this.activeIndex = Math.max(0, this.cards.length - 1);
          }
        }
      } catch (e) {
        console.error("Error redeeming wallet card:", e);
        if (e?.response?.status === 429) {
          await this.bootstrapWallet();
          const updatedCard = this.cards.find((c) => c.stack_id === card.stack_id);
          if (!updatedCard || updatedCard.quantity < requestedFromQty) {
            this.closeRedeemDialog(true);
          } else {
            this.redeemDialogError = "Redemption already in progress. Please wait a moment.";
          }
        } else {
          this.redeemDialogError = "Could not redeem right now. Please try again.";
        }
      } finally {
        this.redeemingKey = null;
        this.redeemSubmitLock = false;
      }
    },
    onRailScroll(e) {
      const el = e.target;
      const width = el.clientWidth || 1;
      this.activeIndex = Math.min(
        this.cards.length - 1,
        Math.max(0, Math.round(el.scrollLeft / width))
      );
    },
    scrollToCard(index) {
      const el = this.$refs.rail;
      if (!el || typeof el.scrollTo !== "function") return;
      const width = el.clientWidth || 1;
      el.scrollTo({ left: width * index, behavior: "smooth" });
    },
    cardStyle(card) {
      if (!card.image) {
        return {
          backgroundImage:
            "linear-gradient(150deg, #232327 0%, #34363d 50%, #24262b 100%)",
        };
      }
      return {
        backgroundImage:
          `linear-gradient(to bottom, rgba(0,0,0,0.06), rgba(0,0,0,0.16)), url("${card.image}")`,
      };
    },
  },
};
</script>

<style scoped>
.wallet-page {
  --wallet-ink: #111827;
  --wallet-muted: rgba(17, 24, 39, 0.56);
  --wallet-accent: #1f2937;
  min-height: 100vh;
  padding: 30px 20px 36px;
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
}

.wallet-shell {
  max-width: 980px;
  margin: 0 auto;
  position: relative;
}

.wallet-content {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}

.wallet-stage {
  position: relative;
  border-radius: 20px;
  padding: 8px 8px 10px;
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.22) 0%,
    rgba(208, 223, 236, 0.34) 100%
  );
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.42);
  animation: walletFadeUp 320ms ease-out 60ms both;
}

.wallet-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
  z-index: 30;
  animation: walletFadeUp 280ms ease-out both;
}

.wallet-heading {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wallet-heading::after {
  content: "";
  width: 46px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(31, 41, 55, 0.65) 0%,
    rgba(31, 41, 55, 0.1) 100%
  );
}

.wallet-title {
  margin: 0;
  color: var(--wallet-ink);
  font-size: clamp(1.4rem, 2.7vw, 2rem);
  font-weight: 650;
  letter-spacing: -0.015em;
  line-height: 1.1;
}

.wallet-meta {
  margin: 0;
  color: var(--wallet-muted);
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.wallet-back-btn {
  color: var(--wallet-muted) !important;
  transition: color 170ms ease;
}

.wallet-back-btn:hover {
  color: var(--wallet-accent) !important;
}

.wallet-rail {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 4px 2px 0;
}

.wallet-rail--single {
  justify-content: center;
}

.wallet-rail::-webkit-scrollbar {
  height: 0;
  display: none;
}

.wallet-rail::-webkit-scrollbar-thumb {
  background: transparent;
}

.wallet-card {
  flex: 0 0 min(430px, calc(100vw - 56px));
  aspect-ratio: 85.60 / 53.98;
  scroll-snap-align: center;
  border-radius: 24px;
  padding: 16px 16px 14px;
  color: #f4f1b3;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  box-shadow: none;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.wallet-card-middle {
  display: block;
  margin-top: 26px;
  padding-top: 0;
  padding-bottom: 0;
}

.wallet-card--image .wallet-card-middle {
  position: absolute;
  left: 16px;
  right: 16px;
  top: 57%;
  margin-top: 0;
  transform: translateY(-50%);
}

.wallet-card-top {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wallet-card-chips-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.wallet-bar {
  margin: 0;
  font-size: clamp(1.2rem, 2.6vw, 1.85rem);
  line-height: 1.04;
  font-weight: 700;
}

.wallet-address {
  margin: 6px 0 0;
  font-size: 0.94rem;
  opacity: 0.92;
}

.wallet-expiry-chip {
  padding: 6px 10px;
  border: 1px solid rgba(244, 241, 179, 0.85);
  border-radius: 999px;
  font-size: 0.74rem;
  letter-spacing: 0.01em;
  white-space: nowrap;
  background: rgba(0, 0, 0, 0.2);
}

.wallet-qty-chip {
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(244, 241, 179, 0.82);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  background: rgba(0, 0, 0, 0.2);
}

.wallet-offer-pill {
  border: 1.5px solid rgba(244, 241, 179, 0.95);
  border-radius: 999px;
  padding: 10px 16px;
  font-size: clamp(1rem, 2.2vw, 1.45rem);
  line-height: 1.12;
  font-weight: 600;
  text-align: center;
  margin: 0;
  width: 100%;
  background: transparent;
  text-shadow: none;
  transform: translateY(0);
}

.wallet-lots-summary {
  margin-top: 12px;
  padding-left: 8px;
  min-height: 58px;
}

.wallet-card--image .wallet-lots-summary {
  position: absolute;
  left: 24px;
  right: 24px;
  top: 69%;
  margin-top: 0;
  padding-left: 0;
  min-height: 0;
}

.wallet-lot-line {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.25;
  color: rgba(244, 241, 179, 0.94);
}

.wallet-lot-line--muted {
  opacity: 0.78;
}

.wallet-lot-more-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.wallet-lot-info-btn {
  width: 13px;
  height: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(244, 241, 179, 0.9);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.15);
  color: rgba(244, 241, 179, 0.95);
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  padding: 0;
  cursor: pointer;
  margin-left: 1px;
  transform: translateY(0);
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricPrecision;
}

.wallet-lot-dialog-title {
  margin-bottom: 8px;
  font-weight: 600;
}

.wallet-lot-dialog-list {
  margin: 0;
  padding-left: 18px;
}

.wallet-redeem-stepper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wallet-redeem-amount {
  min-width: 28px;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
}

.wallet-redeem-error {
  margin: 8px 0 0;
  color: #b42318;
  font-size: 0.85rem;
}

.wallet-card-bottom {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
}

.wallet-card--image .wallet-card-bottom {
  position: absolute;
  right: 16px;
  bottom: 14px;
  margin-top: 0;
}

.wallet-redeem-btn {
  border-radius: 999px !important;
  font-weight: 700 !important;
  padding: 0 16px !important;
  min-height: 34px;
  background: #f4f1b3 !important;
  color: #191919 !important;
  transition: transform 150ms ease, box-shadow 150ms ease, filter 150ms ease;
}

.wallet-redeem-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 7px 16px rgba(7, 10, 19, 0.2);
  filter: brightness(1.02);
}

.wallet-stage-divider {
  height: 1px;
  width: min(280px, 64%);
  margin: 0 auto 0;
  background: linear-gradient(
    90deg,
    rgba(31, 41, 55, 0) 0%,
    rgba(31, 41, 55, 0.18) 25%,
    rgba(31, 41, 55, 0.18) 75%,
    rgba(31, 41, 55, 0) 100%
  );
  opacity: 0.9;
}

.wallet-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  padding: 2px 0 4px;
  animation: walletFadeUp 260ms ease-out 130ms both;
}

.wallet-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(24, 33, 42, 0.22);
  cursor: pointer;
  transition: transform 150ms ease, background-color 150ms ease, opacity 150ms ease;
}

.wallet-dot--active {
  background: rgba(18, 24, 30, 0.82);
  transform: scale(1.2);
}

.wallet-empty {
  min-height: 220px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: grid;
  place-items: center;
  color: rgba(0, 0, 0, 0.62);
  font-weight: 600;
}

.wallet-loading {
  border-radius: 18px;
  overflow: hidden;
}

.wallet-mobile-footer {
  display: none;
}

@media (max-width: 640px) {
  .wallet-page {
    padding: 20px 12px 24px;
  }

  .wallet-header {
    margin-bottom: 14px;
    justify-content: center;
    align-items: center;
  }

  .wallet-title {
    font-size: 1.45rem;
    text-align: center;
  }

  .wallet-heading {
    align-items: center;
  }

  .wallet-meta {
    font-size: 0.69rem;
    text-align: center;
  }

  .wallet-card {
    flex-basis: calc(100% - 16px);
    max-width: calc(100% - 16px);
  }

  .wallet-bar {
    font-size: clamp(1.08rem, 6vw, 1.58rem);
    line-height: 1.02;
  }

  .wallet-address {
    margin-top: 8px;
  }

  .wallet-card-middle {
    margin-top: 22px;
  }

  .wallet-offer-pill {
    padding: 7px 13px;
    font-size: clamp(0.9rem, 4vw, 1.16rem);
    line-height: 1.06;
    border-width: 1.2px;
  }

  .wallet-card-chips-row {
    transform: translateY(-5px);
  }

  .wallet-expiry-chip {
    margin-left: -4px;
    padding: 5px 8px;
    font-size: 0.68rem;
    border-width: 1px;
  }

  .wallet-qty-chip {
    margin-right: -4px;
    padding: 3px 8px;
    font-size: 0.72rem;
    border-width: 1px;
  }

  .wallet-card--image .wallet-card-bottom {
    right: 12px;
    bottom: 12px;
  }

  .wallet-back-btn {
    display: none !important;
  }

  .wallet-content { padding-top: clamp(56px, 18vh, 180px); }

  .wallet-rail {
    gap: 12px;
    padding: 4px 2px 0;
    scroll-padding-left: 0;
  }

  .wallet-dots {
    display: flex !important;
    margin-top: 8px;
  }

  .wallet-mobile-footer {
    display: flex;
    justify-content: center;
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 2px);
    z-index: 40;
    pointer-events: none;
  }

  .wallet-mobile-back-btn {
    min-height: 28px !important;
    padding: 0 8px !important;
    font-size: 0.72rem !important;
    letter-spacing: 0.05em;
    opacity: 0.7;
    text-transform: uppercase;
    pointer-events: auto;
  }

}

@keyframes walletFadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wallet-header,
  .wallet-stage,
  .wallet-dots {
    animation: none;
  }
}
</style>
